import axios, { AxiosInstance, AxiosResponse } from 'axios';
import $http from '@/api';
import store from '@/store';
import uuid from 'uuid/v4';
import extractValuesFromXML from '../utils';

// Estos símbolos añaden propiedades a la clase no detectables
// por Vuex. Esto lo hacemos porque si añadimos instancias de la clase `FileUpload`
// en el state, al cambiar internamente sus propiedades, ej: this.ejemplo = nuevovalor
// detecta que se están haciendo cambios desde fuera de las 'mutaciones' de vuex
// si hacemos this[ejemplo] = nuevovalor ; vuex no se quejará
const uploadId = Symbol('uploadId');
const fileId = Symbol('fileId');
const partsData = Symbol('partsData');
const bytesLoaded = Symbol('bytesLoaded');
const videoId = Symbol('videoId');
const uid = Symbol('uuid');
const onUnloadListener = Symbol('onUnloadListener');


function createOnUnloadListener(abortedFileId, abortedUploadId) {
  return (e) => {
    navigator.sendBeacon(`/api/binaryFile/multipart/${abortedFileId}/abort`, JSON.stringify({ uploadId: abortedUploadId }));
  };
}

/**
 * @description esta clase permite gestionar la subida de un fichero por partes
 *
 * > const fileUpload = new FileUpload(file: File)
 *
 * > await fileUpload.createUpload()
 *
 * > await fileUpload.uploadAllParts()
 *
 * > await fileUpload.completeUpload()
 *
 * @method createUpload()
 * `Inicializa la subida solicitando una url prefirmada y haciendo una petición POST a ésta`
 *
 * @method uploadPart(partNumber,part)
 * `solicita una url prefirmada y hace una petición PUT para subir el fichero a dicha url`
 *
 * @method uploadAllParts()
 * `Sube (ejecuta el método uploadPart) secuencialmente con cada una de las partes`
 *
 * @method completeUpload()
 * `Finaliza la subida multipart`
 *
 * @method upload()
 * `Ejecuta secuencialmente createUpload(), uploadAllParts() y completeUpload()`
 *
 * @method abortUpload()
 * `@todo hacer método`
 *
 * @method retryUpload()
 * `@todo hacer método`
 */
class FileUpload {
  private readonly partSize: number = 10 * 1024 * 1024;

  // 10 mb
  // Usamos esta instancia de axios para la subida de partes (uploadPart)
  // porque la instancia de axios que se usa generalmente en la aplicación
  // y aqui (createUpload y completeUpload) tiene un interceptor
  // que recoge sólo el campo 'response.data' de la respuesta
  // y en el caso de 'uploadPart' necesitamos acceder a los headers
  // de la respuesta
  private http: AxiosInstance = axios.create();

  private file: File;

  private readonly type: string;

  private readonly collectionId: number|null;

  private [onUnloadListener]: EventListener;

  // representa la información que devuelve minio
  // por cada una de las partes subidas
  // usamos esta información para construir el payload
  // de la petición /api/binaryFile/multipart/{id}/complete
  private [partsData]: Array<{ etag: string, partNumber: number }> = [];

  private [uploadId]: string | undefined = undefined;

  private [fileId]: number | undefined = undefined;

  private [bytesLoaded]: Array<number> = [];

  private [uid]: string = uuid()
    .toString();

  private [videoId]: number;

  private readonly fileSize: number;

  private readonly numberParts: number;

  progress: number = 0;

  filename: string;

  contentType: string;

  constructor({
    file,
    type,
    filename,
    collectionId,
  }: { file: File, type: string, filename?: string, collectionId?: number }) {
    this.file = file;
    this.filename = filename || file.name;
    this.fileSize = file.size;
    this.collectionId = collectionId || null;
    this.numberParts = file.size / this.partSize;
    this.contentType = file.type;
    this.type = type;
  }

  get fileId(): number | undefined {
    return this[fileId];
  }

  get uuid(): string {
    return this[uid];
  }

  get videoId(): number {
    return this[videoId];
  }

  set videoId(id: number) {
    this[videoId] = id;
  }

  private get parts(): Set<Blob> {
    const allParts: Set<Blob> = new Set();
    let currentPart: number = 0;
    while (this.numberParts > currentPart) {
      const startSizePart = currentPart * this.partSize;
      const endSizePart = startSizePart + this.partSize;
      const blob = this.file.slice(startSizePart, endSizePart);
      allParts.add(blob);
      currentPart += 1;
    }
    return allParts;
  }

  createUpload(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const createUploadPayload = {
        name: this.filename,
        type: this.type,
        context: store.getters.context,
        extension: this.file.name.split('.').pop(),
        contentType: this.contentType,
        collectionId: this.collectionId,
      };
      const createMultipartPresignedUrlResponse = await $http.post('/api/binaryFile/multipart/init', JSON.stringify(createUploadPayload))
        .catch((err) => {
          reject(err);
        });
      const { id } = createMultipartPresignedUrlResponse;
      const { uploadUrl } = createMultipartPresignedUrlResponse;

      // Asignamos fileId pues lo necesitamos para construir
      // el endpoint de 'UploadPart' y 'CompleteMultipart'
      this[fileId] = id;

      // Es necesario especificar el Content-Type, si no S3 no se entera de lo que estamos subiendo y acaba asociandole
      // application/octet-stream
      // @see: https://mgr.bitban.com/issues/96943
      const createMultipartConf: object = {
        headers: {
          'Content-Type': this.contentType,
        },
      };
      const createMultipartResponse: any = await this.http.post(uploadUrl, null, createMultipartConf)
        .catch((err) => {
          reject(err);
        });

      if (!createMultipartResponse) {
        return;
      }

      // Obtenemos un objeto con el nombre de los nodos del xml
      // indicados en el segundo parámetro como clave y el contenido
      // de dicho nodo como valor
      const createMultipartResponseValues = extractValuesFromXML(createMultipartResponse.data as string, ['UploadId']);

      this[uploadId] = createMultipartResponseValues.UploadId;

      this.setUpListeners();
      resolve();
    });
  }

  uploadPart(partNumber: number, part: Blob): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const uploadPartPayload = {
        uploadId: this[uploadId],
        partNumber,
        contentLength: part.size,
      };
      const uploadPartPresignedUrlResponse = await $http.post(
        `/api/binaryFile/multipart/${this[fileId]}/upload-part`,
        JSON.stringify(uploadPartPayload),
      )
        .catch((err) => {
          reject(err);
        });
      const { uploadUrl } = uploadPartPresignedUrlResponse;
      const uploadPartConfig: object = {
        onUploadProgress: (progressEvent: ProgressEvent): void => {
          const { loaded } = progressEvent;
          this[bytesLoaded][partNumber] = ((loaded / this.fileSize) * 100);
          const progress: number = this[bytesLoaded].reduce((curr, prev) => curr + prev, 0);
          store.commit('upload/UPDATE_FILE_PROGRESS', {
            fileUpload: this,
            progress,
          });
        },
      };
      // Tipo 'any' porque se queja el linter al intentar acceder
      // a la propiedad headers :)
      const uploadPartResponse: AxiosResponse = await this.http.put(uploadUrl, part, uploadPartConfig)
        .catch((err) => {
          reject(err);
        }) as AxiosResponse;

      this[partsData].push({
        etag: uploadPartResponse.headers.etag,
        partNumber,
      });
      resolve();
    });
  }

  uploadAllParts(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // el número de parte comprende enteros
      // desde 1 hasta 100000cientos mil millones
      let partNumber = 1;
      // this.parts.forEach(async(part) { await.this.uploadPart(...)})
      // de esta manera no se ejecuta secuencialmente, necesitamos
      // el bucle for para que el async este en la función superior
      // eslint-disable-next-line
      for (const part of this.parts) {
        await this.uploadPart(partNumber, part);
        partNumber += 1;
      }
      resolve();
    });
  }

  completeUpload(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const completeMultipartPayload = {
        uploadId: this[uploadId],
        parts: this[partsData],
      };
      const response = await $http.post(
        `/api/binaryFile/multipart/${this[fileId]}/complete`,
        JSON.stringify(completeMultipartPayload),
      )
        .catch((err) => {
          reject(err);
        });
      this.detachListeners();
      // Dentro de este commit de la store se actualiza
      // la propiedad 'completed' de esta instancia
      resolve(response);
    });
  }

  setUpListeners(): void {
    this[onUnloadListener] = createOnUnloadListener(this[fileId], this[uploadId]);
    window.addEventListener('unload', this[onUnloadListener], false);
  }

  detachListeners(): void {
    window.removeEventListener('unload', this[onUnloadListener]);
  }

  /**
   * Este método realiza todos los pasos
   * para crear una subida multiparte, subir cada una
   * de sus partes y finalmente completarla
   */
  async upload(): Promise<void> {
    try {
      await this.createUpload();
      await this.uploadAllParts();
      await this.completeUpload();
    } catch (err) {
      store.commit('upload/REJECT_FILE_UPLOAD', this);
    }
  }
}

export default FileUpload;
